import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Settings -> AI Agent, calea noua. Perechile vechi, metoda cu metoda din
 * `application/controllers/settings/Ai_agent.php`:
 *
 *   index                 -> GET  /settings/ai_agent
 *   company               -> GET  /settings/ai_agent/company
 *   save                  -> POST /settings/ai_agent/save
 *   saveCompany           -> POST /settings/ai_agent/company/save
 *   connect               -> POST /settings/ai_agent/connect
 *   testBrain             -> POST /settings/ai_agent/brain/test
 *   saveLab / connectLab / removeLab   -> .../labs[...]
 *   saveTier / addTier / reorderTiers / removeTier -> .../tiers[...]
 *   saveCompanyLimits     -> POST /settings/ai_agent/companies/limits
 *   saveInflightView      -> POST /settings/ai_agent/inflight_view
 *   peopleSearch / savePerson / removePerson  -> .../people[...]
 *   addRunbook / saveRunbookSafe / acceptRunbook / dismissRunbook -> .../runbooks[...]
 *   saveIncident          -> POST /settings/ai_agent/incident
 *   auditData / auditSession / auditPurge     -> .../audit[...]
 *   forgetMemory          -> POST /settings/ai_agent/memory/forget
 *   platform_admins (v4)  -> GET/POST .../platform_admins, .../{id}/remove, .../adam
 *
 * ACELASI ECRAN, DOUA SCOPURI. Rail-ul are cate o intrare in fiecare zona:
 * Global (`settings/ai_agent`) e a platformei, General
 * (`settings/ai_agent/company`) e a firmei din sesiune. `scope` din raspuns
 * spune care, si dupa el se aleg filele.
 *
 * CHEIA NU VINE NICIODATA DINTR-UN GET, ca in portal: randurile poarta doar
 * `has_key`. Connect trimite cheia de la server catre laborator; inapoi vine
 * lista de modele.
 */

export type AiScope = 'global' | 'company'

export type AiOk = { ok: boolean; error?: string | null }

export type AiTier = {
  label: string
  hint: string
  is_default: boolean
  sort: number
  effort: string
  lab: string
  base_url: string
  model: string
  has_key: boolean
  lab_id: number
  speed: string
}

export type AiLabModel = { id: string; name: string }

export type AiLab = {
  id: number
  name: string
  provider: string
  catalog: string
  catalog_name: string
  base_url: string
  has_key: boolean
  models: AiLabModel[]
  connected_at: string
  enabled: boolean
  last_error: string
  /** connected | failed | pending */
  status: string
}

export type AiCatalogEntry = {
  key: string
  name: string
  protocol: string
  base_url: string
  country: string
  note: string
}

export type AiSettings = {
  enabled: boolean
  lab: string
  base_url: string
  model: string
  has_key: boolean
  brain_url: string
  has_brain_token: boolean
  daily_token_budget: number
  /** Treapta implicita a platformei - o CHEIE de treapta, nu neaparat un effort. */
  effort: string
  speed: string
  max_steps: number
  has_steps: boolean
}

export type AiCompanySettings = {
  source: string
  lab: string
  base_url: string
  model: string
  has_key: boolean
  /** Gol = ca globalul. */
  effort: string
  speed: string
  /** 0 = ca globalul. */
  max_steps: number
  lab_id: number
}

export type AiCompanyRow = {
  company_id: number
  name: string
  enabled: boolean
  max_steps: number
  daily_token_budget: number
  people: number
  tokens_today: number
}

export type AiCompanies = {
  rows: AiCompanyRow[]
  has_budget: boolean
  has_access: boolean
  has_steps: boolean
}

export type AiPerson = {
  ID: number
  user_id: number
  company_id: number
  name: string
  /** Prenume si Nume, pentru "People & access" (Prenume Nume). */
  first?: string
  last?: string
  email: string
  company: string
  max_steps: number
  /** Nu se mai scrie din ecran (alocarea in USD il inlocuieste); se trimite inapoi neschimbat. */
  daily_token_budget: number
  enabled: boolean
}

export type AiRunbookSuggestion = {
  fingerprint: string
  company_id: number
  company: string
  text: string
  seen: number
  devices: string[]
  conversations: number[]
  last_seen: string
  commands: string[]
}

export type AiVettedRow = {
  ID: number
  command_title: string
  command: string
  type_name: string | null
  category_name: string | null
  incident_safe: number
  company_id: number
}

export type AiIncidentRow = {
  ID: number
  command_type: string
  command_title: string
  incident_safe: number
  incident_command: string | null
}

export type AiUsageDay = {
  day: string
  messages: number
  tokens_in: number
  tokens_out: number
  cache_read: number
}

export type AiLabUsageModel = {
  model: string
  messages: number
  tokens_in: number
  tokens_out: number
  cost: number | null
  currency: string
  last: string
}

export type AiLabUsage = {
  label: string
  messages: number
  tokens_in: number
  tokens_out: number
  cost: number
  /** false = macar un model n-are pret scris, deci totalul nu e exact. */
  priced: boolean
  currency: string
  models: AiLabUsageModel[]
  last: string
}

/** `null` inseamna "tabela nu e in baza" - nu zero. */
export type AiBadges = {
  sessions: number | null
  commands: number | null
  changes: number | null
  full_access: number | null
}

export type AiFootprint = {
  ready: boolean
  messages?: number
  oldest?: string
  characters?: number
}

export type AiAgentShell = {
  pageTitle: string
  pageDescription: string
  moduleUrl: string
  version: string
  scope: AiScope
  isOwner: boolean
  companyId: number
  companyName: string
  installed: boolean
  ai: AiSettings
  company: AiCompanySettings
  labs: Record<string, { name: string; needs_key: boolean; needs_url: boolean }>
  labCatalog: Record<string, AiCatalogEntry>
  efforts: string[]
  aiLabs: AiLab[]
  labsReady: boolean
  labUsage: Record<string, AiLabUsage>
  tiers: Record<string, AiTier>
  tiersReady: boolean
  customTiersReady: boolean
  companies: AiCompanies | null
  inflightView: string
  usage: { messages: number; tokens_in: number; tokens_out: number }
  usageDaily: AiUsageDay[]
  /** `null` = coloanele catalogului de incident nu exista inca. */
  catalog: AiIncidentRow[] | null
  filters: { search: string; days: number; company_id: number; failed_only: boolean }
  badges: AiBadges
  auditCompanies: { id: number; name: string }[]
  footprint: AiFootprint
  runbooks: AiRunbookSuggestion[]
  runbooksReady: boolean
  people: AiPerson[]
  peopleReady: boolean
  allCompanies: { ID: number; name: string }[]
  cmdTypes: { ID: number; name: string }[]
  cmdCategories: { ID: number; name: string }[]
  vetted: AiVettedRow[]
  vettedSafeReady: boolean
  vettedCompanyReady: boolean
}

const BASE = '/settings/ai_agent'

export function aiAgentShellQuery(scope: AiScope) {
  return queryOptions({
    queryKey: ['settings', 'ai_agent', scope],
    queryFn: () => apiGet<AiAgentShell>(scope === 'company' ? BASE + '/company' : BASE),
  })
}

/* ------------------------------------------------------------------ *
 *  SETARI
 * ------------------------------------------------------------------ */

export function saveAiAgent(fields: Record<string, PostValue>) {
  return apiPost<AiOk>(BASE + '/save', fields)
}

export function saveAiCompany(fields: Record<string, PostValue>) {
  return apiPost<AiOk>(BASE + '/company/save', fields)
}

export function saveAiCompanyLimits(fields: Record<string, PostValue>) {
  return apiPost<AiOk>(BASE + '/companies/limits', fields)
}

export function saveAiInflightView(level: string) {
  return apiPost<AiOk>(BASE + '/inflight_view', { level })
}

/* ------------------------------------------------------------------ *
 *  LABORATOARE
 * ------------------------------------------------------------------ */

export type AiConnectAnswer = {
  ok: boolean
  models: AiLabModel[]
  error: string | null
  /** Laboratorul nu are lista de modele: se scrie id-ul de mana. */
  no_list: boolean
}

export function connectAiLab(fields: Record<string, PostValue>) {
  return apiPost<AiConnectAnswer>(BASE + '/connect', fields)
}

export function testAiBrain(brainUrl: string) {
  return apiPost<{ ok: boolean; text: string }>(BASE + '/brain/test', { brain_url: brainUrl })
}

export type AiSaveLabAnswer = AiOk & { id?: number; connect?: AiConnectAnswer }

export function saveAiLab(fields: Record<string, PostValue>) {
  return apiPost<AiSaveLabAnswer>(BASE + '/labs', fields)
}

export function connectAiLabRow(id: number) {
  return apiPost<AiConnectAnswer>(`${BASE}/labs/${id}/connect`, {})
}

export function removeAiLab(id: number) {
  return apiPost<{ ok: boolean }>(`${BASE}/labs/${id}/remove`, {})
}

/* ------------------------------------------------------------------ *
 *  TREPTE
 * ------------------------------------------------------------------ */

export function saveAiTier(fields: Record<string, PostValue>) {
  return apiPost<AiOk>(BASE + '/tiers', fields)
}

export function addAiTier(fields: Record<string, PostValue>) {
  return apiPost<AiOk & { tier: string }>(BASE + '/tiers/add', fields)
}

export function reorderAiTiers(tiers: string[]) {
  return apiPost<AiOk>(BASE + '/tiers/reorder', { tiers })
}

export function removeAiTier(tier: string) {
  return apiPost<{ ok: boolean }>(BASE + '/tiers/remove', { tier })
}

/* ------------------------------------------------------------------ *
 *  OAMENI
 * ------------------------------------------------------------------ */

export function searchAiPeople(term: string) {
  /* `name` e "Nume Prenume"; `first` / `last` pentru ecranele care scriu "Prenume Nume". */
  return apiGet<{ people: { id: number; name: string; first?: string; last?: string; email: string }[] }>(
    `${BASE}/people/search?q=${encodeURIComponent(term)}`
  )
}

export function saveAiPerson(fields: Record<string, PostValue>) {
  return apiPost<AiOk>(BASE + '/people', fields)
}

export function removeAiPerson(id: number) {
  return apiPost<{ ok: boolean }>(BASE + '/people/remove', { id })
}

/* ------------------------------------------------------------------ *
 *  RUNBOOK-URI SI CATALOG
 * ------------------------------------------------------------------ */

export function addAiRunbook(fields: Record<string, PostValue>) {
  return apiPost<{ ok: boolean; error?: string; id?: number }>(BASE + '/runbooks', fields)
}

export function saveAiRunbookSafe(fields: Record<string, PostValue>) {
  return apiPost<{ ok: boolean; error?: string }>(BASE + '/runbooks/safe', fields)
}

export function acceptAiRunbook(fields: Record<string, PostValue>) {
  return apiPost<{ ok: boolean; error?: string | null; catalog_id?: number }>(BASE + '/runbooks/accept', fields)
}

export function dismissAiRunbook(fields: Record<string, PostValue>) {
  return apiPost<{ ok: boolean }>(BASE + '/runbooks/dismiss', fields)
}

export function saveAiIncident(fields: Record<string, PostValue>) {
  return apiPost<{ ok: boolean; error?: string }>(BASE + '/incident', fields)
}

/* ------------------------------------------------------------------ *
 *  JURNALE
 * ------------------------------------------------------------------ */

export type AiAuditTab = 'sessions' | 'commands' | 'changes' | 'memory'

export type AiAuditRow = Record<string, unknown>

export type AiAuditPage = {
  ready: boolean
  total: number
  rows: AiAuditRow[]
  badges?: AiBadges
}

export type AiAuditQuery = {
  search: string
  days: number
  company_id: number
  failed_only: boolean
  scope: AiScope
  offset: number
  length: number
}

export function fetchAiAudit(tab: AiAuditTab, query: AiAuditQuery) {
  const params = new URLSearchParams({
    search: query.search,
    days: String(query.days),
    company_id: String(query.company_id),
    failed_only: query.failed_only ? '1' : '0',
    scope: query.scope,
    offset: String(query.offset),
    length: String(query.length),
  })

  return apiGet<AiAuditPage>(`${BASE}/audit/${tab}?${params.toString()}`)
}

export type AiTranscriptRow = {
  role: string
  content: string
  steps_json: string | null
  tokens_in: number
  tokens_out: number
  created_at: string
}

export function fetchAiSession(conversationId: number) {
  return apiGet<{ ready: boolean; rows: AiTranscriptRow[] }>(`${BASE}/audit/session/${conversationId}`)
}

export function purgeAiTranscripts(days: number) {
  return apiPost<{ ok: boolean; error?: string; removed?: number }>(BASE + '/audit/purge', { days })
}

export function forgetAiMemory(id: number, companyId: number) {
  return apiPost<{ ok: boolean }>(BASE + '/memory/forget', { id, company_id: companyId })
}

/* ------------------------------------------------------------------ *
 *  ADMINISTRATORII DE PLATFORMA SI TREPTELE LUI ADAM
 *
 *  Nu are pereche in portal: lista era scrisa in cod
 *  (`AgentReleasePublishers`). Din 15 septembrie 2026 sta in
 *  `it_platform_admins`, cu plasa pe codul din clasa cand tabela lipseste
 *  sau e goala. Numai un administrator de platforma vede si scrie.
 * ------------------------------------------------------------------ */

export type PlatformAdmin = {
  /** 0 = rand din lista din cod (plasa), nu din tabela - nu se poate scoate. */
  id: number
  email: string
  added_by: number
  added_by_name: string
  added_at: string
  note: string
}

export type AdamAccessRow = {
  user_id: number
  name: string
  email: string
  access: number
  models: number
  role_access: number
  role_models: number
  /** Cati Adam deodata (implicit 1, ca pana acum). */
  parallel?: number
  /** De unde are accesul: pe cont (`own`), din rol (`role`), administrator de platforma. */
  sources?: ('own' | 'role' | 'platform_admin')[]
}

export type PlatformAdminsPage = {
  ready: boolean
  fallback: boolean
  me: string
  admins: PlatformAdmin[]
  adamReady: boolean
  adam: AdamAccessRow[]
  /** Administratorii de platforma fara cont de utilizator gasit dupa adresa. */
  adminsWithoutAccount?: string[]
  /** 0 None, 1 Investigation, 2 Change, 3 Incident, 4 Full access */
  actions: string[]
  /** 0 None, 1 Quick, 2 Balance, 3 Deep, 4 Deepest */
  depths: string[]
  /** false = tabela `it_ai_parallel_limits` lipseste: limita nu se poate scrie. */
  parallelReady?: boolean
  parallelMax?: number
}

export function platformAdminsQuery() {
  return queryOptions({
    queryKey: ['settings', 'ai_agent', 'platform_admins'],
    queryFn: () => apiGet<PlatformAdminsPage>(BASE + '/platform_admins'),
  })
}

export function addPlatformAdmin(email: string, note: string) {
  return apiPost<AiOk>(BASE + '/platform_admins', { email, note })
}

export function removePlatformAdmin(id: number) {
  return apiPost<AiOk>(`${BASE}/platform_admins/${id}/remove`, {})
}

export function saveAdamAccess(userId: number, access: number, models: number, parallel?: number) {
  const body: Record<string, number> = { user_id: userId, access, models }
  if (parallel !== undefined) body.parallel = parallel
  return apiPost<AiOk & { warning?: string }>(BASE + '/platform_admins/adam', body)
}

/* ------------------------------------------------------------------ *
 *  MODEL PENTRU ALARME
 *
 *  Cu ce model se face diagnosticul AUTOMAT al alarmelor, pe firma
 *  (`it_ai_alarm_model`; regulile in `Application\Services\Ai\AlarmModel`).
 *  Fara rand = implicitul ieftin: auto, Haiku 5.5 -> Sonnet 5.5, refolosire
 *  24h. Numai un administrator de platforma vede si scrie.
 * ------------------------------------------------------------------ */

export type AlarmModelOverride = { kind: string; mode: 'auto' | 'fixed'; model: string }

export type AlarmModelSettings = {
  mode: 'auto' | 'fixed'
  fixed_model: string
  ceiling: string
  /** Treapta de pornire la Auto; implicit Haiku 5.5. Poate fi "<ID laborator>:<model>". */
  start?: string
  overrides: AlarmModelOverride[]
  dedup: boolean
  dedup_hours: number
  /** Zgomot cunoscut: tipare (cu `*`) pe tip sau serviciu; nu merg la model. */
  noise: string[]
  /** Aceeasi alarma (tip + serviciu) pe alta masina: diagnosticul de acolo. */
  fleet: boolean
}

export type AlarmModelCompany = {
  id: number
  name: string
  custom: boolean
  settings: AlarmModelSettings
  /** Furnizorii permisi in buget (chei de catalog); null = toti. */
  allowedLabs?: string[] | null
}

/** Un model de ales: Claude (value = numele) sau al unui laborator ("<ID>:<model>"). */
export type AlarmModelChoice = {
  value: string
  model: string
  lab_id: number
  lab_name: string
  /** Cheia din catalog (anthropic, openai, moonshot...) - aceeasi ca in allowed_labs. */
  catalog: string
  /** Din it_ai_model_prices, pe 1M jetoane; null = fara pret. */
  price: { in: number; out: number; read: number | null; currency: string } | null
  /** Costul estimat al unei alarme (profilul mediu); null = fara pret. */
  per_alarm: number | null
}

export type AlarmModelPage = {
  ready: boolean
  missing: string
  /** Scara, de jos in sus: Haiku 5.5, Sonnet 5.5, Opus 5.5. */
  models: string[]
  /** Toate modelele, Claude intai, apoi pe laborator. */
  choices?: AlarmModelChoice[]
  defaults: AlarmModelSettings
  maxHours: number
  maxNoise: number
  companies: AlarmModelCompany[]
  alarmTypes: { id: number; name: string }[]
}

export function alarmModelQuery() {
  return queryOptions({
    queryKey: ['settings', 'ai_agent', 'alarm_model'],
    queryFn: () => apiGet<AlarmModelPage>(BASE + '/alarm_model'),
  })
}

export function saveAlarmModel(companyId: number, s: AlarmModelSettings) {
  return apiPost<AiOk>(BASE + '/alarm_model', {
    company_id: companyId,
    mode: s.mode,
    fixed_model: s.fixed_model,
    ceiling: s.ceiling,
    start: s.start ?? 'claude-haiku-5-5',
    /* Ca text JSON: lista de exceptii nu incape curat in campuri de formular. */
    overrides: JSON.stringify(s.overrides),
    dedup: s.dedup ? 1 : 0,
    dedup_hours: s.dedup_hours,
    noise: JSON.stringify((s.noise ?? []).map((p) => p.trim()).filter((p) => p !== '')),
    fleet: s.fleet === false ? 0 : 1,
  })
}

export function resetAlarmModel(companyId: number) {
  return apiPost<AiOk>(`${BASE}/alarm_model/${companyId}/reset`, {})
}

/* ------------------------------------------------------------------ *
 *  BUGETE SI RAPORTUL DE COST
 *
 *  Bugetul lui Adam in bani, pe firma si pe om, furnizorii permisi si cum
 *  vede omul costul (`it_ai_budget_company`, `it_ai_budget_users`; regulile
 *  in `Application\Services\Ai\Spend`). Fara rand = implicitul: buget
 *  nesetat (nimic nu se opreste), la 100% cheap_only, prag 80, toti
 *  furnizorii, USD. Setarile le scrie numai un administrator de platforma;
 *  raportul il vede si adminul firmei, doar pentru firma lui.
 * ------------------------------------------------------------------ */

export type BudgetAtLimit = 'stop' | 'cheap_only' | 'cheap_extra'

export type BudgetDisplay = 'usd' | 'credits' | 'hidden'

export type BudgetCompanySettings = {
  monthly_usd: number
  warn_pct: number
  at_limit: BudgetAtLimit
  extra_usd: number
  user_default_usd: number
  /** null = toti furnizorii (si cei adaugati mai tarziu). */
  allowed_labs: string[] | null
  display: BudgetDisplay
  credit_usd: number
  /** Treapta implicita a oamenilor: '' = ca azi (alege omul), `auto`, sau o treapta fixa. */
  human_tier?: string
}

export type BudgetCompany = {
  id: number
  name: string
  custom: boolean
  settings: BudgetCompanySettings
  spent_usd: number
  state: 'ok' | 'warn' | 'cheap' | 'stop'
}

export type BudgetPage = {
  ready: boolean
  missing: string
  defaults: BudgetCompanySettings
  atLimit: BudgetAtLimit[]
  display: BudgetDisplay[]
  cheapModel: string
  /** Optiunile treptei implicite a oamenilor ('' = ca azi). */
  humanTiers?: { key: string; label: string }[]
  labs: { key: string; name: string }[]
  month: string
  companies: BudgetCompany[]
}

export type BudgetPersonSettings = {
  inherit: boolean
  unlimited: boolean
  monthly_usd: number
  at_limit: BudgetAtLimit | ''
  extra_usd: number
}

/** Alocarea efectiva: randul omului, altfel cea implicita a firmei (`Spend::allowance`). */
export type BudgetAllowance = { unlimited: boolean; usd: number; at_limit: BudgetAtLimit; extra_usd: number; from: 'company' | 'own' }

/** Alocarea unui om din lista de acces la Adam, pe firma lui (`angajati.FirmaID`). */
export type BudgetAllocation = {
  user_id: number
  company_id: number
  company: string
  custom: boolean
  settings: BudgetPersonSettings
  allowance: BudgetAllowance
  spent_usd: number
}

export type BudgetAllocations = { ready: boolean; missing: string; rows: BudgetAllocation[] }

export type Money = { form: BudgetDisplay; value: number | null; text: string }

export type CostMonth = {
  label: string
  total: Money
  companies: { id: number; name: string; cost: Money; budget: Money | null; pct: number | null }[]
  users: { company_id: number; company: string; user_id: number; name: string; cost: Money }[]
  models: { model: string; messages: number; tokens_in: number; tokens_out: number; cache_read: number; priced: boolean; cost: Money }[]
  unpriced: string[]
}

export type CostReport = {
  scope: 'platform' | 'company'
  form: BudgetDisplay
  months: CostMonth[]
}

export function budgetQuery() {
  return queryOptions({
    queryKey: ['settings', 'ai_agent', 'budget'],
    queryFn: () => apiGet<BudgetPage>(BASE + '/budget'),
  })
}

export function budgetAllocationsQuery(userIds: number[]) {
  const ids = [...userIds].sort((x, y) => x - y).join(',')
  return queryOptions({
    queryKey: ['settings', 'ai_agent', 'budget', 'allocations', ids],
    queryFn: () => apiGet<BudgetAllocations>(`${BASE}/budget/allocations?ids=${ids}`),
    enabled: ids !== '',
  })
}

export function costReportQuery() {
  return queryOptions({
    queryKey: ['settings', 'ai_agent', 'budget', 'report'],
    queryFn: () => apiGet<CostReport>(BASE + '/budget/report'),
  })
}

export function saveBudget(companyId: number, s: BudgetCompanySettings) {
  return apiPost<AiOk>(BASE + '/budget', {
    company_id: companyId,
    monthly_usd: s.monthly_usd,
    warn_pct: s.warn_pct,
    at_limit: s.at_limit,
    extra_usd: s.extra_usd,
    user_default_usd: s.user_default_usd,
    /* "all" = NULL pe server: si un furnizor adaugat mai tarziu e permis. */
    allowed_labs: s.allowed_labs === null ? 'all' : JSON.stringify(s.allowed_labs),
    display: s.display,
    credit_usd: s.credit_usd,
    human_tier: s.human_tier ?? '',
  })
}

export function resetBudget(companyId: number) {
  return apiPost<AiOk>(`${BASE}/budget/${companyId}/reset`, {})
}

export function saveBudgetPerson(companyId: number, userId: number, s: BudgetPersonSettings) {
  return apiPost<AiOk>(BASE + '/budget/person', {
    company_id: companyId,
    user_id: userId,
    unlimited: s.unlimited ? 1 : 0,
    monthly_usd: s.monthly_usd,
    at_limit: s.at_limit,
    extra_usd: s.extra_usd,
  })
}

export function resetBudgetPerson(companyId: number, userId: number) {
  return apiPost<AiOk>(`${BASE}/budget/${companyId}/person/${userId}/reset`, {})
}
